import { apiClient } from '@/api/client';
import { API_USER_TERMS_AND_CONDITIONS_ACCEPT } from '@/api/endpoints';
import { USER_QUERY_KEY } from '@/api/queryKeys';
import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { LegalSection } from '@/components/LegalSection';
import { LogoutConfirmDialog } from '@/components/LogoutConfirmDialog';
import { ROUTE_HOME } from '@/router/routes';
import { ButtonVariant, Size } from '@/types/components';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

export const TermsAndConditions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);

  const { mutateAsync: acceptTerms, isPending } = useMutation({
    mutationFn: () => apiClient.post(API_USER_TERMS_AND_CONDITIONS_ACCEPT),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: USER_QUERY_KEY }),
  });

  const handleAcceptTerms = async () => {
    if (isPending) {
      return;
    }

    try {
      await acceptTerms();
      navigate(ROUTE_HOME, { replace: true });
    } catch (error) {
      logRequestError(ErrorMessage.AcceptTermsAndConditions, error);
    }
  };

  return (
    <div className="min-h-screen bg-background md:flex md:h-screen md:flex-col">
      <div className="mx-auto flex w-full max-w-[1142px] flex-col gap-6 px-4 pt-[calc(24px+env(safe-area-inset-top))] pb-[calc(200px+env(safe-area-inset-bottom))] md:flex-1 md:min-h-0 md:px-8 md:py-12 md:pb-0">
        <header className="flex shrink-0 flex-col gap-3">
          <h1 className="text-title-36 text-foreground">
            {t('terms.pageTitle')}
          </h1>
          <div className="flex flex-col gap-3">
            {t('terms.description', { returnObjects: true }).map(
              (paragraph) => (
                <p
                  key={paragraph}
                  className="text-body-18 text-muted-foreground"
                >
                  {paragraph}
                </p>
              ),
            )}
          </div>
        </header>

        <section className="flex flex-col gap-8 rounded-2xl bg-card px-6 py-5 md:min-h-0 md:flex-1 md:overflow-y-auto">
          <h2 className="text-title-24 text-foreground">
            {t('terms.card.title')}
          </h2>
          <div className="flex flex-col gap-6">
            {t('terms.card.sections', { returnObjects: true }).map(
              (section) => (
                <LegalSection
                  key={section.title}
                  title={section.title}
                  points={section.points}
                />
              ),
            )}
          </div>
        </section>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-border bg-background md:static md:shrink-0 md:border-t-0">
        <footer className="mx-auto flex w-full max-w-[1142px] flex-col items-center gap-4 px-4 pt-4 pb-[calc(24px+env(safe-area-inset-bottom))] md:px-8 md:pt-6 md:pb-12">
          <label className="flex items-center gap-2">
            <Checkbox checked={isAgreed} onChange={setIsAgreed} />
            <span className="text-body-16 text-foreground">
              {t('terms.agreementLabel')}
            </span>
          </label>
          <div className="flex gap-2">
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.Large}
              onClick={() => setIsLogoutConfirmOpen(true)}
              className="w-40"
            >
              {t('global.logout')}
            </Button>
            <Button
              variant={ButtonVariant.Primary}
              size={Size.Large}
              onClick={handleAcceptTerms}
              disabled={!isAgreed}
              isLoading={isPending}
              className="w-40"
            >
              {t('global.continue')}
            </Button>
          </div>
        </footer>
      </div>
      <LogoutConfirmDialog
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
      />
    </div>
  );
};
